import SearchCriteria, { LocationType } from "../../models/SearchCriteria";
import { SubFsmBuilder } from "little-fsm";
import { HomeFsmContext } from "./HomeFsmContext";
import { ArticleDTO, BookingDTO, BookingSummaryDTO, InternationalStringDTO, LocationDTO, SourceLocationDTO } from "../../apiclient/ApiDtos";
import SearchFormModel from "../../models/SearchFormModel";

export type HomeStateFsmScheme = {
    context: HomeFsmContext,
    substates: [
        'initial',
        'selecting_location',
        'fetching_article',
        'showing_article',
        'showing_contact',
        'fetching_my_booking_list',
        'showing_my_booking_list',
        'fetching_booking_details',
        'showing_my_booking_details',
        'showing_booking_details_error'
    ],
    events: {
        'back': {},
        "booking_category_changed": { isSearchingSmallCar: boolean, updatedSearchFormModel: SearchFormModel }
        "location_requested": { locationType: LocationType }
        "location_selected": { locationType: LocationType, location: LocationDTO }

        "search_requested": { searchCriteria: SearchCriteria },

        "article_requested": { slug: string, title: InternationalStringDTO },
        "article_received": { article: ArticleDTO },

        "contact_requested": {},

        "my_bookings_requested": {},
        "my_bookings_received": { bookings: BookingSummaryDTO[] },
        "my_booking_details_requested": {},
        "booking_details_received": { booking: BookingDTO },
        "booking_details_error": {}
    }
}

export function homeStateSubFsmBuilder(): SubFsmBuilder<HomeStateFsmScheme> {
    let subBuilder = new SubFsmBuilder<HomeStateFsmScheme>();

    subBuilder.entrySubstate('initial')

    subBuilder.substate("initial")
        .transition('article_requested', 'fetching_article',
            (context, event) => context.withArticleToLoad(event.slug, event.title))
        .transition('contact_requested', 'showing_contact',
            (context, event) => context)
        .transition('booking_category_changed', 'initial',
            (context, event) => context.withBookingCategoryChanged(event.isSearchingSmallCar, event.updatedSearchFormModel))
        .transition('back', 'initial', (context, event) => context)
        .transition('location_requested', 'selecting_location',
            (context, event) => new HomeFsmContext(context.searchFormModel, context.isSearchingSmallCar, event.locationType))
        .transition('my_bookings_requested', 'fetching_my_booking_list', (c, e) => c)
        .completion('search_requested', (context, event) => context);

    subBuilder.substate("selecting_location")
        .transition('back', 'initial',
            (context, event) => new HomeFsmContext(context.searchFormModel, context.isSearchingSmallCar))
        .transition('location_selected', 'initial', (context, event) => {
            let searchFormModel = context.searchFormModel;
            if (event.locationType == 'source') {
                searchFormModel.sourceLocation = <SourceLocationDTO>event.location;
                if (searchFormModel.destinationLocation && searchFormModel.destinationLocation.id == searchFormModel.sourceLocation.id) {
                    searchFormModel.destinationLocation = null;
                }
            } else {
                searchFormModel.destinationLocation = event.location;
            }
            return new HomeFsmContext(searchFormModel, context.isSearchingSmallCar);
        });

    subBuilder.substate('fetching_article')
        .transition('article_received', 'showing_article',
            (context, event) => context.withArticle(event.article))
        .transition('back', "initial",
            (context, event) => context);

    subBuilder.substate('showing_article')
        .transition('back', "initial", (context, event) => context);

    subBuilder.substate('showing_contact')
        .transition('contact_requested', 'showing_contact', (c) => c)
        .transition('back', "initial", (context, event) => context);

    subBuilder.substate('fetching_my_booking_list')
        .transition('my_bookings_received', 'showing_my_booking_list',
            (context, event) => context.withMyBookings(event.bookings))
        .transition('back', "initial", (context, event) => context);

    subBuilder.substate('showing_my_booking_list')
        .transition('my_booking_details_requested', 'fetching_booking_details', (c, e) => c)
        .transition('back', "initial", (context, event) => context);

    subBuilder.substate('fetching_booking_details')
        .transition('booking_details_received', 'showing_my_booking_details', (c, e) => c)
        .transition('back', "showing_my_booking_list", (context, event) => context);

    subBuilder.substate('showing_my_booking_details')
        .transition('back', "showing_my_booking_list", (context, event) => context);

    return subBuilder;
}